// Magaza stratejisi editoru — "ayni tasarim, magazaya gore uretim".
//
// Bir tasarim bu magazaya gonderilirken burada tanimlanan kurallar uygulanir:
// baslik sablonu, etiket stratejisi, fiyat kurali, liste varsayilanlari.
// Urun tipi secilirse strateji SADECE o tip icin gecerlidir (kupa ile tisort
// ayni magazada farkli fiyatlanabilir); "Tum urunler" magazanin genelidir.

import { useEffect, useState } from "react";
import { Loader2, Save, Trash2, Plus, AlertCircle, CheckCircle } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

interface Strategy {
  id: string;
  productTypeKey: string;
  name: string;
  mockupTemplateIds: string[];
  mediaProfile: Record<string, unknown> | null;
  seoProfile: Record<string, unknown> | null;
  pricingRule: Record<string, unknown> | null;
  listingDefaults: Record<string, unknown> | null;
  isActive: boolean;
}

interface ProductType { key: string; label: string; vertical: string }
interface Collection { id: string; name: string; productTypeKey?: string | null }

const EMPTY_FORM = {
  productTypeKey: "",
  name: "Varsayilan",
  titleTemplate: "",
  extraTags: "",
  excludeTags: "",
  descriptionSuffix: "",
  multiplier: "",
  addFixed: "",
  fixedPrice: "",
  currency: "USD",
  rounding: "none",
  imageLimit: "",
  // Bu magazanin kullanacagi mockup gruplari (MockupCollection id'leri)
  mockupCollectionIds: [] as string[],
};

type FormState = typeof EMPTY_FORM;

function toForm(s: Strategy): FormState {
  const seo = (s.seoProfile ?? {}) as Record<string, unknown>;
  const price = (s.pricingRule ?? {}) as Record<string, unknown>;
  const media = (s.mediaProfile ?? {}) as Record<string, unknown>;
  const arr = (v: unknown) => (Array.isArray(v) ? (v as string[]).join(", ") : "");
  const num = (v: unknown) => (v == null ? "" : String(v));
  return {
    productTypeKey: s.productTypeKey || "",
    name: s.name || "Varsayilan",
    titleTemplate: typeof seo.titleTemplate === "string" ? seo.titleTemplate : "",
    extraTags: arr(seo.extraTags),
    excludeTags: arr(seo.excludeTags),
    descriptionSuffix: typeof seo.descriptionSuffix === "string" ? seo.descriptionSuffix : "",
    multiplier: num(price.multiplier),
    addFixed: num(price.addFixed),
    fixedPrice: num(price.fixedPrice),
    currency: typeof price.currency === "string" ? price.currency : "USD",
    rounding: price.rounding === "99" ? "99" : "none",
    imageLimit: num(media.imageLimit),
    mockupCollectionIds: Array.isArray(s.mockupTemplateIds) ? s.mockupTemplateIds : [],
  };
}

function toPayload(f: FormState) {
  const list = (v: string) => v.split(",").map((t) => t.trim()).filter(Boolean);
  const num = (v: string) => (v.trim() === "" ? undefined : Number(v));
  return {
    productTypeKey: f.productTypeKey,
    name: f.name,
    seoProfile: {
      ...(f.titleTemplate ? { titleTemplate: f.titleTemplate } : {}),
      ...(f.extraTags ? { extraTags: list(f.extraTags) } : {}),
      ...(f.excludeTags ? { excludeTags: list(f.excludeTags) } : {}),
      ...(f.descriptionSuffix ? { descriptionSuffix: f.descriptionSuffix } : {}),
    },
    pricingRule: {
      ...(num(f.multiplier) ? { multiplier: num(f.multiplier) } : {}),
      ...(num(f.addFixed) ? { addFixed: num(f.addFixed) } : {}),
      ...(num(f.fixedPrice) ? { fixedPrice: num(f.fixedPrice) } : {}),
      currency: f.currency || "USD",
      rounding: f.rounding,
    },
    mediaProfile: {
      ...(num(f.imageLimit) ? { imageLimit: num(f.imageLimit) } : {}),
    },
    mockupTemplateIds: f.mockupCollectionIds,
  };
}

export function ShopStrategyEditor({ shopId }: { shopId: string }) {
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [editing, setEditing] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const [sRes, pRes, cRes] = await Promise.all([
        fetch(`${getBackendUrl()}/api/etsy/shops/${shopId}/strategies`, { headers: authHeaders() }),
        fetch(`${getBackendUrl()}/api/product-types`, { headers: authHeaders() }),
        fetch(`${getBackendUrl()}/api/mockups/collections`, { headers: authHeaders() }),
      ]);
      if (sRes.ok) setStrategies((await sRes.json()).data ?? []);
      if (pRes.ok) setProductTypes((await pRes.json()).data ?? []);
      if (cRes.ok) setCollections((await cRes.json()).data ?? []);
    } catch {
      /* ag hatasi — kullanici tekrar deneyebilir */
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [shopId]);

  async function save() {
    if (!editing) return;
    setBusy(true); setError(null); setMsg(null);
    try {
      const res = await fetch(`${getBackendUrl()}/api/etsy/shops/${shopId}/strategies`, {
        method: "PUT", headers: authHeaders(), body: JSON.stringify(toPayload(editing)),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Kaydedilemedi");
      setMsg("Strateji kaydedildi");
      setEditing(null);
      await load();
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("Bu strateji silinsin mi? Mağaza varsayılan davranışa döner.")) return;
    setBusy(true);
    try {
      await fetch(`${getBackendUrl()}/api/etsy/shops/${shopId}/strategies/${id}`, {
        method: "DELETE", headers: authHeaders(),
      });
      await load();
    } finally {
      setBusy(false);
    }
  }

  const typeLabel = (key: string) =>
    key ? (productTypes.find((p) => p.key === key)?.label ?? key) : "Tüm ürünler";

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-800">Üretim Stratejisi</h4>
          <p className="text-xs text-gray-500">
            Bir tasarım bu mağazaya gönderilirken uygulanacak kurallar. Tanımlanmazsa
            ürün neyse o gönderilir (mevcut davranış).
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing({ ...EMPTY_FORM })}
            className="flex shrink-0 items-center gap-1 rounded-lg border border-indigo-200 bg-white px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
          >
            <Plus className="h-3.5 w-3.5" /> Strateji ekle
          </button>
        )}
      </div>

      {msg && (
        <p className="mb-2 flex items-center gap-1 text-xs text-emerald-700">
          <CheckCircle className="h-3.5 w-3.5" /> {msg}
        </p>
      )}
      {error && (
        <p className="mb-2 flex items-center gap-1 text-xs text-red-600">
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </p>
      )}

      {/* Mevcut stratejiler */}
      {strategies.length === 0 && !editing && (
        <p className="text-xs text-gray-400">Henüz strateji yok — varsayılan davranış geçerli.</p>
      )}

      <div className="space-y-1.5">
        {strategies.map((s) => {
          const seo = (s.seoProfile ?? {}) as Record<string, unknown>;
          const price = (s.pricingRule ?? {}) as Record<string, unknown>;
          return (
            <div key={s.id} className="flex items-start justify-between gap-2 rounded border border-gray-200 bg-white px-3 py-2">
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-800">
                  {typeLabel(s.productTypeKey)}
                  <span className="ml-1.5 font-normal text-gray-400">{s.name}</span>
                </p>
                <p className="mt-0.5 truncate text-[11px] text-gray-500">
                  {typeof seo.titleTemplate === "string" && seo.titleTemplate ? `Başlık: ${seo.titleTemplate}` : "Başlık: üründen"}
                  {price.multiplier ? ` · Fiyat x${price.multiplier}` : ""}
                  {price.fixedPrice ? ` · Sabit ${price.fixedPrice}` : ""}
                  {price.currency ? ` ${price.currency}` : ""}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <button type="button" onClick={() => setEditing(toForm(s))}
                  className="rounded px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-100">Düzenle</button>
                <button type="button" onClick={() => remove(s.id)}
                  className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Duzenleme formu */}
      {editing && (
        <div className="mt-3 space-y-3 rounded-lg border border-indigo-200 bg-white p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className="mb-0.5 block text-[11px] font-medium text-gray-600">Ürün tipi</label>
              <select
                value={editing.productTypeKey}
                onChange={(e) => setEditing({ ...editing, productTypeKey: e.target.value })}
                className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs"
              >
                <option value="">Tüm ürünler (mağaza geneli)</option>
                {productTypes.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-0.5 block text-[11px] font-medium text-gray-600">Strateji adı</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs" />
            </div>
          </div>

          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Başlık ve etiket</p>
            <label className="mb-0.5 block text-[11px] text-gray-600">Başlık şablonu</label>
            <input
              value={editing.titleTemplate}
              onChange={(e) => setEditing({ ...editing, titleTemplate: e.target.value })}
              placeholder="{title} | Handmade Gift"
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs"
            />
            <p className="mt-0.5 text-[10px] text-gray-400">
              <code>{"{title}"}</code> ürünün kendi başlığıyla değişir. Boş bırakılırsa başlık olduğu gibi gider.
            </p>

            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <div>
                <label className="mb-0.5 block text-[11px] text-gray-600">Eklenecek etiketler</label>
                <input value={editing.extraTags} onChange={(e) => setEditing({ ...editing, extraTags: e.target.value })}
                  placeholder="virgülle ayırın" className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs" />
              </div>
              <div>
                <label className="mb-0.5 block text-[11px] text-gray-600">Çıkarılacak etiketler</label>
                <input value={editing.excludeTags} onChange={(e) => setEditing({ ...editing, excludeTags: e.target.value })}
                  placeholder="virgülle ayırın" className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs" />
              </div>
            </div>

            <label className="mb-0.5 mt-2 block text-[11px] text-gray-600">Açıklama sonuna eklenecek</label>
            <textarea value={editing.descriptionSuffix} onChange={(e) => setEditing({ ...editing, descriptionSuffix: e.target.value })}
              rows={2} placeholder="Kargo/iade notu, mağaza imzası..."
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs" />
          </div>

          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Fiyat</p>
            <div className="grid gap-2 sm:grid-cols-4">
              <div>
                <label className="mb-0.5 block text-[11px] text-gray-600">Çarpan</label>
                <input value={editing.multiplier} onChange={(e) => setEditing({ ...editing, multiplier: e.target.value })}
                  placeholder="1.2" className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs" />
              </div>
              <div>
                <label className="mb-0.5 block text-[11px] text-gray-600">Ekle</label>
                <input value={editing.addFixed} onChange={(e) => setEditing({ ...editing, addFixed: e.target.value })}
                  placeholder="5" className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs" />
              </div>
              <div>
                <label className="mb-0.5 block text-[11px] text-gray-600">Sabit fiyat</label>
                <input value={editing.fixedPrice} onChange={(e) => setEditing({ ...editing, fixedPrice: e.target.value })}
                  placeholder="—" className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs" />
              </div>
              <div>
                <label className="mb-0.5 block text-[11px] text-gray-600">Para birimi</label>
                <select value={editing.currency} onChange={(e) => setEditing({ ...editing, currency: e.target.value })}
                  className="w-full rounded border border-gray-200 px-2 py-1.5 text-xs">
                  <option value="USD">USD</option>
                  <option value="TRY">TRY</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-600">
              <input type="checkbox" checked={editing.rounding === "99"}
                onChange={(e) => setEditing({ ...editing, rounding: e.target.checked ? "99" : "none" })}
                className="h-3.5 w-3.5" />
              Fiyatı ,99'a yuvarla
            </label>
            <p className="mt-0.5 text-[10px] text-gray-400">
              Sabit fiyat doluysa çarpan ve ekleme yok sayılır.
            </p>
          </div>

          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Görsel</p>

            {/* Bu magazanin kullanacagi mockup gruplari — ayni tasarim baska
                magazada baska gruplarla uretilebilsin diye magaza bazinda secilir. */}
            <label className="mb-1 block text-[11px] text-gray-600">Mockup grupları</label>
            {collections.length === 0 ? (
              <p className="text-[11px] text-gray-400">Henüz mockup grubu yok (Mockup Yönetimi'nden oluşturun).</p>
            ) : (
              <div className="max-h-40 space-y-1 overflow-y-auto rounded border border-gray-200 p-2">
                {collections.map((c) => {
                  const on = editing.mockupCollectionIds.includes(c.id);
                  // Urun tipi secilmisse o tipe ait olmayan gruplar soluk gosterilir
                  const mismatch = !!editing.productTypeKey && !!c.productTypeKey && c.productTypeKey !== editing.productTypeKey;
                  return (
                    <label key={c.id} className={`flex cursor-pointer items-center gap-2 text-[11px] ${mismatch ? "opacity-40" : ""}`}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => setEditing({
                          ...editing,
                          mockupCollectionIds: on
                            ? editing.mockupCollectionIds.filter((id) => id !== c.id)
                            : [...editing.mockupCollectionIds, c.id],
                        })}
                        className="h-3.5 w-3.5"
                      />
                      <span className="flex-1 truncate text-gray-700">{c.name}</span>
                      <span className="text-gray-400">
                        {c.productTypeKey
                          ? (productTypes.find((p) => p.key === c.productTypeKey)?.label ?? c.productTypeKey)
                          : "tipsiz"}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="mt-0.5 text-[10px] text-gray-400">
              Boş bırakılırsa Studio'da seçilen gruplar kullanılır.
            </p>

            <label className="mb-0.5 mt-2 block text-[11px] text-gray-600">Bu mağazaya gönderilecek görsel sayısı</label>
            <input value={editing.imageLimit} onChange={(e) => setEditing({ ...editing, imageLimit: e.target.value })}
              placeholder="boş = hepsi" className="w-32 rounded border border-gray-200 px-2 py-1.5 text-xs" />
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={save} disabled={busy}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Kaydet
            </button>
            <button type="button" onClick={() => { setEditing(null); setError(null); }}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
              İptal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
